import React, { useEffect, useState } from "react";

interface Props {
  stageId: number | null;
  processingId: number;
}

// How fast the bar fills. About 60% after one time constant, 82% after two, 90% after three.
// Set it to about 40% of a typical extraction time (20 second extractions -> 8).
const TIME_CONSTANT_SECONDS = 8;

// The bar never shows more than this while the row is still extracting
const MAX_WHILE_RUNNING = 95;

const STORAGE_PREFIX = "extractionStart:";

// Start time per row, kept outside the component so it survives re-renders and remounts.
// Also saved in sessionStorage so a page reload in the same tab keeps the bar going.
const startTimes = new Map<number, number>();

const getStartTime = (processingId: number): number => {
  const cached = startTimes.get(processingId);
  if (cached) return cached;

  let start = Date.now();
  try {
    const saved = sessionStorage.getItem(STORAGE_PREFIX + processingId);
    if (saved && Number(saved) > 0) {
      start = Number(saved);
    } else {
      sessionStorage.setItem(STORAGE_PREFIX + processingId, String(start));
    }
  } catch {
    // sessionStorage not available: the in-memory value is enough
  }
  startTimes.set(processingId, start);
  return start;
};

// 1% at the start, approaching MAX_WHILE_RUNNING as time passes
const percentAt = (elapsedSeconds: number): number => {
  const p =
    1 +
    (MAX_WHILE_RUNNING - 1) * (1 - Math.exp(-elapsedSeconds / TIME_CONSTANT_SECONDS));
  return Math.min(MAX_WHILE_RUNNING, Math.max(1, Math.round(p)));
};

// stageId 1 = queued, 7 = extracting (confirm against your ProcessStages table)
const ExtractionProgress: React.FC<Props> = ({ stageId, processingId }) => {
  const extracting = stageId === 7;
  const [pct, setPct] = useState(1);

  useEffect(() => {
    if (!extracting) return;
    const start = getStartTime(processingId);
    const update = () => setPct(percentAt((Date.now() - start) / 1000));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [extracting, processingId]);

  if (extracting) {
    return (
      <div className="ep">
        <span className="ep-label">
          <span className="ep-spinner" />
          {pct}% extracted
        </span>
        <div className="ep-track">
          <div className="ep-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  if (stageId === 1) {
    return (
      <div className="ep">
        <span className="ep-label">Queued for extraction</span>
        <div className="ep-track" />
      </div>
    );
  }
  if (stageId === 4) {
    return (
      <div className="ep">
        <span className="ep-label">Failed to extract</span>
        <div className="ep-track" />
      </div>
    );
  }

  // any other stage: the job just finished or failed and the row is about to leave
  return (
    <div className="ep">
      <span className="ep-label">Moving to Inbox…</span>
      <div className="ep-track">
        <div className="ep-fill" style={{ width: "100%" }} />
      </div>
    </div>
  );
};

export default ExtractionProgress;